import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import {
  BillingPage,
  BillingPanel,
  BillingTableHead,
  StatusChip,
  btnGhost,
} from './billingUi';
import {
  fetchBillingAccountByTenant,
  fetchBillingAccounts,
  upsertBillingAccount,
  fetchTaxRules,
} from '../../services/billingApi';
import { fetchTenants, fetchLookups, fetchTenantSubscription } from '../../services/licensingApi';

const emptyForm = {
  legalCompanyName: '',
  billingContact: '',
  billingEmail: '',
  billingAddress: '',
  country: '',
  currency: 'USD',
  paymentTerms: 'NET_30',
  taxId: '',
  taxRatePercent: '0',
  annualUpfrontDiscountPercent: '0',
  taxRuleCode: '',
  region: '',
  taxExempt: false,
  active: true,
};

/** Spec §8.2 Billing Account Screen */
const BillingAccounts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [lookups, setLookups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tenantId, setTenantId] = useState(searchParams.get('tenantId') || '');
  const [form, setForm] = useState(emptyForm);
  const [accountExists, setAccountExists] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [taxRules, setTaxRules] = useState([]);

  const currencies = useMemo(() => {
    const rows = lookups.CURRENCY || [];
    const mapped = rows
      .map((c) => {
        if (c == null) return null;
        if (typeof c === 'string') return { code: c, label: c };
        const code = String(c.code ?? c.lookupCode ?? c.value ?? '').trim();
        if (!code) return null;
        return { code, label: String(c.label ?? c.lookupLabel ?? code) };
      })
      .filter(Boolean);
    return mapped.length ? mapped : [{ code: 'USD', label: 'USD' }];
  }, [lookups.CURRENCY]);

  const paymentTerms = useMemo(() => {
    const rows = lookups.PAYMENT_TERMS || [];
    const mapped = rows
      .map((c) => {
        if (c == null) return null;
        if (typeof c === 'string') return { code: c, label: c };
        const code = String(c.code ?? c.lookupCode ?? c.value ?? '').trim();
        if (!code) return null;
        return { code, label: String(c.label ?? c.lookupLabel ?? code) };
      })
      .filter(Boolean);
    return mapped.length ? mapped : [{ code: 'NET_30', label: 'Net 30' }];
  }, [lookups.PAYMENT_TERMS]);

  const countries = useMemo(() => {
    const rows = lookups.BILLING_COUNTRY || [];
    const mapped = rows
      .map((c) => {
        if (c == null) return null;
        const code = String(c.code ?? '').trim();
        if (!code) return null;
        return { code, label: String(c.label ?? code) };
      })
      .filter(Boolean);
    return mapped.length
      ? mapped
      : [
          { code: 'India', label: 'India' },
          { code: 'United States', label: 'United States' },
        ];
  }, [lookups.BILLING_COUNTRY]);

  const regionsForCountry = useMemo(() => {
    const rows = lookups.BILLING_REGION || [];
    return rows
      .map((r) => {
        const code = String(r?.code ?? '');
        const sep = code.indexOf('::');
        const country = sep > 0 ? code.slice(0, sep) : '';
        const regionName = sep > 0 ? code.slice(sep + 2) : String(r?.label || code);
        return {
          code,
          label: String(r?.label || regionName),
          country,
          regionName,
        };
      })
      .filter((r) => r.country === form.country);
  }, [lookups.BILLING_REGION, form.country]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [acc, tens, luk, rules] = await Promise.all([
        fetchBillingAccounts(),
        fetchTenants(),
        fetchLookups({ grouped: true }),
        fetchTaxRules(),
      ]);
      setAccounts(acc);
      setTenants(tens);
      setLookups(luk || {});
      setTaxRules(rules);
    } catch (e) {
      setError(e.message || 'Failed to load billing accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const tid = searchParams.get('tenantId');
    if (tid) openEdit(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tenantOptions = useMemo(
    () =>
      (tenants || []).map((t) => ({
        id: String(t.tenantId ?? t.tenant_id),
        name: t.tenantName || t.tenant_name || `Tenant ${t.tenantId ?? t.tenant_id}`,
      })),
    [tenants]
  );

  const openEdit = async (tid) => {
    const id = String(tid);
    setTenantId(id);
    setSearchParams({ tenantId: id });
    setSubscription(null);
    try {
      const [account, sub] = await Promise.all([
        fetchBillingAccountByTenant(id),
        fetchTenantSubscription(id),
      ]);
      setSubscription(sub);
      if (!account) {
        setAccountExists(false);
        setDialogOpen(false);
        toast.info('A billing account is created only when the tenant is registered.');
        return;
      }
      setAccountExists(true);
      setDialogOpen(true);
      setForm({
        legalCompanyName: account.legalCompanyName || '',
        billingContact: account.billingContact || '',
        billingEmail: account.billingEmail || '',
        billingAddress: account.billingAddress || '',
        country: account.country || '',
        currency: account.currency || 'USD',
        paymentTerms: account.paymentTerms || 'NET_30',
        taxId: account.taxId || '',
        taxRatePercent: account.taxRatePercent != null ? String(account.taxRatePercent) : '0',
        annualUpfrontDiscountPercent:
          account.annualUpfrontDiscountPercent != null ? String(account.annualUpfrontDiscountPercent) : '0',
        taxRuleCode: account.taxRuleCode || '',
        region: account.region || '',
        taxExempt: !!account.taxExempt,
        active: account.active !== false,
      });
    } catch (e) {
      toast.error(e.message || 'Failed to open account');
    }
  };

  const handleSave = async () => {
    if (!tenantId || !accountExists) {
      toast.error('This account was created with the tenant. Open Edit on an existing row.');
      return;
    }
    setSaving(true);
    try {
      await upsertBillingAccount(tenantId, form);
      toast.success('Billing account saved');
      await load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BillingPage>
      <BillingNav
        title="Billing Accounts"
        subtitle="Created with the tenant. Use this screen to update the bill-to contact, tax, and payment terms."
        actions={
          <Button startIcon={<Refresh />} variant="outlined" sx={btnGhost} onClick={load}>
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <BillingPanel tone="blue">
          <TableContainer>
            <Table size="small">
              <BillingTableHead
                columns={[
                  { label: 'Tenant' },
                  { label: 'Legal company' },
                  { label: 'Contact' },
                  { label: 'Email' },
                  { label: 'Country' },
                  { label: 'Currency' },
                  { label: 'Terms' },
                  { label: 'Active' },
                  { label: '', align: 'right' },
                ]}
              />
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 5, textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        No billing accounts yet. One is created when a tenant is registered.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((a) => (
                    <TableRow key={a.billingAccountId} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {a.tenantName || a.tenantId}
                        </Typography>
                      </TableCell>
                      <TableCell>{a.legalCompanyName}</TableCell>
                      <TableCell>{a.billingContact || '—'}</TableCell>
                      <TableCell>{a.billingEmail || '—'}</TableCell>
                      <TableCell>{a.country || '—'}</TableCell>
                      <TableCell>{a.currency}</TableCell>
                      <TableCell>{a.paymentTerms}</TableCell>
                      <TableCell>
                        {a.active === false ? (
                          <StatusChip status="EXPIRED" label="No" />
                        ) : (
                          <StatusChip status="ACTIVE" label="Yes" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" sx={btnGhost} onClick={() => openEdit(a.tenantId)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </BillingPanel>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit billing account</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tenant</InputLabel>
                <Select label="Tenant" value={tenantId} disabled>
                  {tenantOptions.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Legal company name"
                value={form.legalCompanyName}
                onChange={(e) => setForm((f) => ({ ...f, legalCompanyName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Billing contact"
                value={form.billingContact}
                onChange={(e) => setForm((f) => ({ ...f, billingContact: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Billing email"
                value={form.billingEmail}
                onChange={(e) => setForm((f) => ({ ...f, billingEmail: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Billing address"
                value={form.billingAddress}
                onChange={(e) => setForm((f) => ({ ...f, billingAddress: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  label="Country"
                  value={form.country || ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, country: e.target.value, region: '' }))
                  }
                >
                  <MenuItem value="">
                    <em>Select country</em>
                  </MenuItem>
                  {countries.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                  {form.country && !countries.some((c) => c.code === form.country) ? (
                    <MenuItem value={form.country}>{form.country}</MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select
                  label="Currency"
                  value={form.currency || ''}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                >
                  {currencies.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                  {form.currency && !currencies.some((c) => c.code === form.currency) ? (
                    <MenuItem value={form.currency}>{form.currency}</MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment terms</InputLabel>
                <Select
                  label="Payment terms"
                  value={form.paymentTerms || ''}
                  onChange={(e) => setForm((f) => ({ ...f, paymentTerms: e.target.value }))}
                >
                  {paymentTerms.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                  {form.paymentTerms &&
                  !paymentTerms.some((c) => c.code === form.paymentTerms) ? (
                    <MenuItem value={form.paymentTerms}>{form.paymentTerms}</MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Tax ID"
                value={form.taxId}
                onChange={(e) => setForm((f) => ({ ...f, taxId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={!form.country}>
                <InputLabel>Region / State</InputLabel>
                <Select
                  label="Region / State"
                  value={form.region || ''}
                  onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                >
                  <MenuItem value="">
                    <em>Select region</em>
                  </MenuItem>
                  {regionsForCountry.map((r) => (
                    <MenuItem key={r.code} value={r.regionName || r.label}>
                      {r.label}
                    </MenuItem>
                  ))}
                  {form.region &&
                  !regionsForCountry.some((r) => (r.regionName || r.label) === form.region) ? (
                    <MenuItem value={form.region}>{form.region}</MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Tax %"
                value={form.taxRatePercent}
                onChange={(e) => setForm((f) => ({ ...f, taxRatePercent: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Annual upfront discount %"
                value={form.annualUpfrontDiscountPercent}
                onChange={(e) => setForm((f) => ({ ...f, annualUpfrontDiscountPercent: e.target.value }))}
                helperText="Used on the next yearly invoice. Leave 0 for none."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tax rule</InputLabel>
                <Select
                  label="Tax rule"
                  value={form.taxRuleCode || ''}
                  onChange={(e) => setForm((f) => ({ ...f, taxRuleCode: e.target.value }))}
                >
                  <MenuItem value="">Account tax % only</MenuItem>
                  {taxRules.map((r) => (
                    <MenuItem key={r.ruleCode} value={r.ruleCode}>
                      {r.ruleName || r.ruleCode} ({r.ratePercent}%)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.taxExempt}
                    onChange={(e) => setForm((f) => ({ ...f, taxExempt: e.target.checked }))}
                  />
                }
                label="Tax exempt"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.active !== false}
                    onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          {subscription && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Subscription summary
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" fontWeight={650}>
                  {subscription.planName || subscription.planCode || '—'}
                </Typography>
                <StatusChip status={subscription.subscriptionStatus} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {subscription.billingCycle || '—'}
                {' · '}
                {subscription.startDate || '—'} to {subscription.endDate || '—'}
                {' · '}
                Auto-renew {subscription.autoRenew ? 'Yes' : 'No'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={btnGhost}
                  onClick={() => navigate(`/admin/billing/subscriptions?tenantId=${tenantId}`)}
                >
                  View subscription
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={btnGhost}
                  onClick={() => navigate(`/admin/billing/invoices?tenantId=${tenantId}`)}
                >
                  View invoices
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={btnGhost}
                  onClick={() => navigate(`/admin/billing/payments?tenantId=${tenantId}`)}
                >
                  View payments
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            disabled={saving || !tenantId}
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </BillingPage>
  );
};

export default BillingAccounts;
